import { css } from "@emotion/react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import { theme } from "@polifonia/theme";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { ExternalLink } from "@/components/ExternalLink";
import { Typography } from "@/components/Typography";

const cssCard = css`
  display: flex;
  flex-direction: column;
  border: 1px solid ${theme.colors.staticFgLine};
  border-radius: 12px;
  overflow: hidden;
`;

const cssCardImageWrapper = css`
  width: 100%;
  height: 88px;
  background-color: ${theme.colors.staticBgContainer};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const cssCardImage = css`
  width: 220px;
  height: 88px;
  object-fit: contain;
`;

const cssCardContent = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  flex: 1;
`;

const cssCardFooter = css`
  display: block;
  padding: 0 16px 16px 16px;
`;

const cssLoadingContainer = css`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  background-color: ${theme.colors.white};
  border: 1px solid ${theme.colors.staticFgLine};
  border-radius: 4px;
`;

const cssConnectedBadge = css`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 20px;
  background-color: ${theme.colors.staticBgPositive};
  color: ${theme.colors.staticFgPositive};
  font-size: 12px;
  font-weight: 500;
`;

const cssInfoBanner = css`
  width: 100%;
  margin-top: 8px;
  padding: 8px 12px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
  background-color: ${theme.colors.staticBgPrimary};
  color: ${theme.colors.staticFgPrimary};
  font-size: 14px;
  font-weight: 500;
  border: none;
  outline: none;
  box-shadow: none;
  text-align: left;
  justify-content: flex-start;
  cursor: pointer;
  font-family: inherit;
`;

const cssTitleContainer = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
`;

type ButtonComponentProps = React.ComponentProps<typeof Button>;

export interface ApplicationCardProps {
  title: string;
  description: string;
  learnMoreLink?: string;
  learnMoreText?: string;
  buttonText: string;
  buttonVariant?: "primary" | "plain";
  onButtonClick?: () => void;
  buttonProps?: Partial<ButtonComponentProps>;
  imageUrl?: string;
  isConnected?: boolean;
  isLoading?: boolean;
  connectedLabel?: string;
  connectedButtonText?: string;
  infoBannerText?: string;
  infoBannerLink?: string;
  infoBannerIcon?: React.ReactNode;
  detailText?: string | undefined;
}

export const ApplicationCard: React.FC<ApplicationCardProps> =
  function ApplicationCard({
    title,
    description,
    learnMoreLink,
    learnMoreText,
    buttonText,
    buttonVariant = "primary",
    onButtonClick,
    buttonProps,
    imageUrl,
    isConnected,
    isLoading,
    connectedLabel,
    connectedButtonText,
    infoBannerText,
    infoBannerLink,
    infoBannerIcon,
    detailText,
  }) {
    const { t } = useTranslation();
    return (
      <Card css={cssCard} variant="outlined">
        {imageUrl && (
          <div css={cssCardImageWrapper}>
            <img src={imageUrl} alt={title} css={cssCardImage} />
          </div>
        )}
        <CardContent css={cssCardContent}>
          <div css={cssTitleContainer}>
            <Typography
              variant="h3"
              fontWeight={500}
              css={css`
                font-size: 16px;
              `}
            >
              {title}
            </Typography>
            {isConnected ? (
              <span css={cssConnectedBadge}>{connectedLabel}</span>
            ) : null}
          </div>
          <Typography
            variant="body"
            css={css`
              font-size: 14px;
            `}
          >
            {description}
          </Typography>
          {detailText ? (
            <Box
              component="span"
              sx={{ color: theme.colors.staticFgNote, fontWeight: 500 }}
            >
              <Typography variant="note">{detailText}</Typography>
            </Box>
          ) : null}
          {learnMoreLink && learnMoreText && (
            <ExternalLink href={learnMoreLink} trailingIcon={true}>
              {learnMoreText}
            </ExternalLink>
          )}
          {infoBannerText ? (
            infoBannerLink ? (
              <ExternalLink href={infoBannerLink} css={cssInfoBanner}>
                {infoBannerIcon}
                {infoBannerText}
              </ExternalLink>
            ) : (
              <div css={cssInfoBanner}>
                {infoBannerIcon}
                {infoBannerText}
              </div>
            )
          ) : null}
        </CardContent>
        <div css={cssCardFooter}>
          {isLoading ? (
            <div css={cssLoadingContainer}>
              <CircularProgress size={20} />
            </div>
          ) : (
            <Button
              variant={isConnected ? "plain" : buttonVariant}
              onClick={onButtonClick}
              fullWidth={true}
              css={[
                isConnected &&
                  css`
                    border: 1px solid ${theme.colors.colorBlue60};
                    color: ${theme.colors.colorBlue60};
                  `,
              ]}
              {...buttonProps}
            >
              {isConnected
                ? (connectedButtonText ?? t("common.edit"))
                : buttonText}
            </Button>
          )}
        </div>
      </Card>
    );
  };
